/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Wed May 22 2024
 **/

import { useState } from "react";

import {
	Button,
	Column,
	Icon,
	Row,
	Tab,
	TabContainer,
	TabContent,
	TabItem,
	TextField,
	Tooltip,
	Typography,
	useToast
} from "@/frameworks/designStuff/components";
import { Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";

import { colors } from "@/frameworks/core/styles";
import { usePhoneCall, usePopover, useSimplePopup } from "@/frameworks/core/hooks";
import { APIErrorCode, ModalID, POPOVER_ID } from "@/frameworks/core/commons";

import GroupPeopleList from "./GroupPeopleList";
import PhoneFriend from "./PhoneFriend";
import SelectedUser from "./SelectedUser";
import { useGroupCall } from "@/frameworks/core/API";
import * as S from "./emotion";

type AddCalleesPopupProps = {
	callID: string;
};

export default function AddCalleesPopup(props: AddCalleesPopupProps) {
	const phoneCall = usePhoneCall();
	const callUsers = phoneCall.state.users(props.callID)?.[0];
	const [callees, setCallees] = phoneCall.state.callees;
	const popover = usePopover();
	const toast = useToast();
	const simplePopup = useSimplePopup();
	const [searchValue, setSearchValue] = useState("");
	const participatedUserList = phoneCall.state.participatedUserList(props.callID);
	//통화 시작 전에는 callees로 초기화, 통화시작 후에는 participateduserList로 초기화.
	const [selectedUserList, setSelectedUserList] = useState<WebServices.User.UserInfo[]>(
		props.callID
			? callUsers && [
					...Object.values(callUsers)
						.map((user) => ({ ...user, userID: user.userId ?? "" }))
						.filter((user) => participatedUserList.includes(user.userID))
						.filter((user) => user.userID !== Configure.userID)
				]
			: [
					...callees
						.filter((user) => participatedUserList.includes(user.userID))
						.filter((user) => user.userID !== Configure.userID)
				]
	);

	const handleClose = () => {
		popover.hide(POPOVER_ID.ADD_CALL_USER);
	};

	const updateUserListToCall = (userID: string, name: string) => {
		// callees는 콜에 거쳐간 사람이 모두있고,
		// participatedUsrList에는 현재 참여중인 사람만 있음.
		if (participatedUserList.includes(userID)) {
			toast.show({ type: "error", message: _T("최초 시작 멤버는 삭제 불가합니다.") });
			return;
		}

		const removeIDFromList = selectedUserList.filter((user) => user.userID !== userID);
		if (selectedUserList.map((user) => user.userID).includes(userID)) {
			setSelectedUserList(removeIDFromList);
		} else {
			if (removeIDFromList.length < 3) {
				setSelectedUserList((prev) => [...prev, { userID: userID, name: name }]);
			} else {
				//TODO 추후 기획 요구사항 반영 필요
				toast.show({ type: "error", message: _T("최대 3명까지 선택할 수 있습니다") });
			}
		}
	};

	const groupCallAPI = useGroupCall();
	const addUserToCallList = () => {
		if (phoneCall.exists(props.callID)) {
			groupCallAPI.add
				.execute({
					callId: props.callID,
					userIds: selectedUserList
						.filter((user) => callUsers?.[user.userID]?.status !== "ACCEPTED")
						.map((user) => user.userID)
				})
				.catch((error) => {
					switch (error.code) {
						case APIErrorCode.RECEIVER_ACTIVE_CALL_EXISTS: {
							simplePopup(ModalID.CALL_EXIST_POPUP, _T("상대방이 이미 통화 중입니다."));
							return;
						}

						case APIErrorCode.DATA_NOT_FOUND: {
							simplePopup(ModalID.CALL_EXIST_POPUP, _T("존재하지 않는 상대방이 포함되어 있습니다."));
							return;
						}

						case APIErrorCode.ALL_RECEIVER_BUSY: {
							simplePopup(ModalID.CALL_EXIST_POPUP, _T("상대방이 이미 통화 중입니다."));
							return;
						}
					}
				});
			setCallees(selectedUserList);
		} else {
			setCallees(selectedUserList);
		}
		popover.hide(POPOVER_ID.ADD_CALL_USER);
	};

	return (
		<S.AddUserWrapper>
			<Column style={{ padding: "24px 26px 12px" }} gap={12}>
				<Row horizonAlign="distribute" style={{ width: "100%" }}>
					<Typography typoSize="Head6">{_T("통화 상대 추가")}</Typography>
					<Button.Icon name="Close24" variant="text" size="large" onClick={() => handleClose()} />
				</Row>
				<TextField.Icon
					style={{ width: "100%" }}
					onChange={(ev) => setSearchValue(ev.target.value)}
					leftIcon={<Icon name="Search24" size={24} fill={colors.neutral_30_100} />}
				/>
			</Column>
			<Row gap={28} style={{ width: "100%" }} horizonAlign="center">
				{[...Array(3)].map((_, index) => {
					const selectedUser = selectedUserList?.[index];
					const statusUserConfirm = selectedUser && participatedUserList?.includes(selectedUser.userID);
					if (selectedUserList?.[index]) {
						return (
							<SelectedUser
								key={index}
								userID={selectedUserList?.[index].userID}
								statusUserConfirm={statusUserConfirm}
								onUserSelected={updateUserListToCall}
							/>
						);
					} else {
						return <S.EmptyUser key={index} />;
					}
				})}
			</Row>
			<div css={{ background: colors.neutral_50_8, width: "100%", height: "100%" }}>
				<TabContainer activeId="group">
					<Tab>
						<TabItem id="group" style={{ width: "50%", textAlign: "center" }}>
							<Typography.p typoSize="Title3">{_T("그룹")}</Typography.p>
						</TabItem>
						<TabItem id="friend" style={{ width: "50%", textAlign: "center" }}>
							<Typography.p typoSize="Title3">{_T("친구")}</Typography.p>
						</TabItem>
					</Tab>
					<TabContent activeId="group">
						<GroupPeopleList
							selectedUserList={selectedUserList}
							searchValue={searchValue}
							onUserSelected={updateUserListToCall}
						/>
					</TabContent>
					<TabContent style={{ height: "100%" }} activeId="friend">
						<PhoneFriend
							selectedUserList={selectedUserList}
							searchValue={searchValue}
							onUserSelected={updateUserListToCall}
						/>
					</TabContent>
				</TabContainer>
			</div>
			<Row style={{ width: "100%", padding: "0px 20px 20px" }}>
				{selectedUserList?.length === 1 ? (
					<Tooltip hasIcon={false} message={_T("멤버가 2명 이상 추가되어야 합니다.")}>
						<Button
							css={{ width: "316px" }}
							size="Xlarge"
							onClick={addUserToCallList}
							disabled={selectedUserList.length === 1}
						>
							{_T("추가하기")}
						</Button>
					</Tooltip>
				) : (
					<Button css={{ width: "316px" }} size="Xlarge" onClick={addUserToCallList}>
						{_T("추가하기")}
					</Button>
				)}
			</Row>
		</S.AddUserWrapper>
	);
}
